import * as _ from "lodash";
import * as React from "react";
import styled, { css } from "styled-components";
import { v1 as uuidv1 } from "uuid";
import { toast } from "react-toastify";

import { getMessageFromErrors } from "src/library/utils/errors/apiError";
import { Button } from "src/library/components/Button";
import Table from "src/library/components/Tables/Table";
import Grid from "src/library/components/Hedron";
import { PageProps } from "src/pages/Router/routes";
import { useProductTagsApi } from "src/modules/product-tags/api/product-tags.api";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  adminProductTagsTableColumns,
  IProcuctTagTemplateTableRow
} from "src/core/shared/components/tables/adminProductTagsTableColumns";
import { Icons } from "src/library/components/Icon/types";

/**
 * Main "Product Tags" View
 *
 * @returns
 */
const ProductTagsManagementPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const [ listProductTagsResponse, listProductTagsRequest ] = useProductTagsApi("LIST_PRODUCT_TAGS");
  const [ createTagsResponse, createTagsRequest ] = useProductTagsApi("BULK_CREATE_PRODUCT_TAG");
  const [ updateTagsResponse, updateTagsRequest ] = useProductTagsApi("BULK_UPDATE_PRODUCT_TAG");
  const [ deleteTagsResponse, deleteTagsRequest ] = useProductTagsApi("BULK_DELETE_PRODUCT_TAG");
  const [ tableData, setTableData ] = React.useState<IProcuctTagTemplateTableRow[]>([]);
  const [ toSave, setToSave ] = React.useState<string[]>([]);
  const [ toCreate, setToCreate ] = React.useState<string[]>([]);
  const [ toDelete, setToDelete ] = React.useState<IProcuctTagTemplateTableRow[]>([]);
  const [ isSaving, setIsSaving ] = React.useState(false);

  /**
   * Handle delete of a row by uid
   */
  const onRowDelete = React.useCallback(
    (uid: string) => {
      const changedIndex = _.findIndex(tableData, d => d.uid === uid);
      const row = tableData[ changedIndex ];

      if (row && row.id !== 0) {
        setToDelete([ ...toDelete, row ]);
      }

      setTableData(_.remove(tableData, d => d.uid !== uid));
    },
    [ toDelete, tableData ]
  );

  /**
   * handle a change to the row data
   */
  const onRowChange = React.useCallback(
    <T extends keyof IProcuctTagTemplateTableRow>(property: T, value: IProcuctTagTemplateTableRow[T], uid: string) => {
      const changedIndex = _.findIndex(tableData, d => d.uid === uid);
      const row = tableData[ changedIndex ];

      if (row) {
        row[ property ] = value;
      }

      // keep track of to save vs create
      if (row.id === 0) {
        setToCreate(_.uniq([ ...toCreate, row.uid ]));
      } else {
        setToSave(_.uniq([ ...toSave, row.uid ]));
      }

      setTableData([ ...tableData ]);
    },
    [
      tableData,
      toCreate,
      toSave
    ]
  );

  // Handle any responses from the get
  React.useEffect(() => {
    if (listProductTagsResponse.data) {
      setTableData(_.map(listProductTagsResponse.data, d => ({
        ...d,
        uid: uuidv1()
      })));
    }
  }, [ listProductTagsResponse ]);

  /**
   * Add a new row to the table data
   */
  const addNewRow = React.useCallback(() => {
    const uid = uuidv1();

    setToCreate([ ...toCreate, uid ]);

    setTableData([
      {
        id: 0,
        icon: Icons.ADVISORY,
        label: "",
        info: "",
        type: "Advisory",
        uid
      },
      ...tableData
    ]);
  }, [ tableData, toCreate ]);

  /**
   * Trigger requests for save/update/deletion
   */
  const saveAllProductTags = React.useCallback(() => {
    const toCreateTags = _.compact(_.map(toCreate, uid => _.find(tableData, d => d.uid === uid)));
    const toUpdateTags = _.compact(_.map(toSave, uid => _.find(tableData, d => d.uid === uid)));

    if (!toCreateTags.length && !toUpdateTags.length && !toDelete.length) {
      toast.warn("No changes to save!");

      return;
    }

    if (toCreateTags.length) {
      createTagsRequest(toCreateTags);
    }

    if (toUpdateTags.length) {
      updateTagsRequest(toUpdateTags);
    }

    if (toDelete.length) {
      deleteTagsRequest(_.map(toDelete, "id"));
    }
  }, [
    toCreate,
    toSave,
    toDelete
  ]);

  // Handle the update response
  React.useEffect(() => {
    const newToSave = [];

    if (updateTagsResponse.data) {
      if (updateTagsResponse.data.errors.length) {
        // attach the errors to the rows
        for (const rowError of updateTagsResponse.data.errors) {
          const rowIndex = _.findIndex(tableData, tableRow => tableRow.id === rowError.data.id);

          if (tableData[ rowIndex ]) {
            newToSave.push(tableData[ rowIndex ].uid);
            tableData[ rowIndex ].error = getMessageFromErrors(rowError);
          }
        }
      }

      if (updateTagsResponse.data.data.length) {
        // attach the returned data to the rows
        for (const rowData of updateTagsResponse.data.data) {
          const rowIndex = _.findIndex(tableData, tableRow => tableRow.id === rowData.id);

          if (tableData[ rowIndex ]) {
            tableData[ rowIndex ] = {
              ...tableData[ rowIndex ],
              ...rowData,
              error: undefined
            };
          }
        }

        setToSave(newToSave);
        setTableData([ ...tableData ]);
        toast.success("Successfully updated product tags.");
      }
    }
  }, [ updateTagsResponse.data ]);

  // Handle the create response
  React.useEffect(() => {
    const newToCreate = [];

    if (createTagsResponse.data) {
      if (createTagsResponse.data.errors.length) {
        // attach any errors to the rows
        for (const rowError of createTagsResponse.data.errors) {
          const rowIndex = _.findIndex(tableData, tableRow => tableRow.id === rowError.data.id);

          if (tableData[ rowIndex ]) {
            newToCreate.push(tableData[ rowIndex ].uid);
            tableData[ rowIndex ].error = getMessageFromErrors(rowError);
          }
        }
      }

      if (createTagsResponse.data.data.length) {
        // attach the returned data to the rows
        for (const rowData of createTagsResponse.data.data) {
          const rowIndex = _.findIndex(
            tableData,
            tableRow => tableRow.id === 0 && tableRow.label === rowData.label && tableRow.type === rowData.type
          );

          if (tableData[ rowIndex ]) {
            tableData[ rowIndex ] = {
              ...tableData[ rowIndex ],
              ...rowData,
              error: undefined
            };
          }
        }

        setToCreate(newToCreate);
        setTableData([ ...tableData ]);
        toast.success("Successfully created product tags.");
      }
    }
  }, [ createTagsResponse.data ]);

  // Handle the delete response
  React.useEffect(() => {
    const undeleted: IProcuctTagTemplateTableRow[] = [];

    if (deleteTagsResponse.data) {
      if (deleteTagsResponse.data.errors.length) {
        for (const rowError of deleteTagsResponse.data.errors) {
          const unDeletedRow = _.find(toDelete, d => rowError.data.id === d.id);

          if (unDeletedRow) {
            undeleted.push({
              ...unDeletedRow,
              error: getMessageFromErrors(rowError)
            });
          }
        }
      }

      toast.success("Successfully deleted product tags.");
      setToDelete([]);
    }

    setTableData([ ...tableData, ...undeleted ]);
  }, [ deleteTagsResponse.data ]);

  // Handle saving state
  React.useEffect(() => {
    if (!updateTagsResponse.pending && !createTagsResponse.pending && !deleteTagsResponse.pending) {
      setIsSaving(false);
    } else {
      setIsSaving(true);
    }
  }, [
    deleteTagsResponse.pending,
    updateTagsResponse.pending,
    createTagsResponse.pending
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Grid.Box>
          <Button
            disabled={isSaving}
            onClick={() => addNewRow()}
          >
            New tag
          </Button>
        </Grid.Box>

        <StyledTable
          error={listProductTagsResponse.error}
          data={tableData}
          onFetchData={() => listProductTagsRequest({})}
          tableId="global"
          tableOf="Product Tags"
          filterable={false}
          columns={adminProductTagsTableColumns({
            onRowChange,
            onRowDelete
          })}
          pages={1}
          manual={false}
          showPagination={false}
          showPageSizeOptions={false}
          showFilters={false}
          loading={listProductTagsResponse.pending}
          totalRows={listProductTagsResponse.data ? listProductTagsResponse.data.length : 0}
        />

        <Grid.Box>
          <Button
            loading={isSaving}
            onClick={() => saveAllProductTags()}
          >
            Save
          </Button>
        </Grid.Box>
      </MainContentWrapper>
    </Layout>
  );
};

const StyledTable = styled(Table)`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing.padding.lg};

    .rt-table {
      overflow: visible !important;
      overflow-y: visible !important;
      overflow-x: visible !important;
      .rt-tbody {
        background-color: transparent !important;
      }

      .rt-tr,
      .rt-tr-group {
        border-bottom: none !important;
      }

      .rt-tr {
        justify-content: flex-start !important;
      }

      .icon-column {
        overflow: visible !important;
      }
    }
  `}
`;

export default ProductTagsManagementPage;
